import React, { useState, useEffect } from 'react'
import { Package, Download } from 'lucide-react'
import { materialsApi } from '@/services/api'
import { Button } from '@/components/ui/Button'

interface InventoryStats {
  totalValue: number
  totalItems: number
  lowStockItems: number
  outOfStockItems: number
  averageValue: number
}

interface MaterialWithCalculations {
  id: string
  name: string
  category: string
  currentStock: number
  reorderPoint?: number
  averageCost?: number
  lastPurchasePrice?: number
  lastCostPerUnit?: number
  lastPurchaseDate?: string
  preferredSupplier?: string
  calculatedValue: number
  stockStatus: 'in-stock' | 'low-stock' | 'out-of-stock'
}

export const InventoryAnalysisReport: React.FC = () => {
  const [inventoryData, setInventoryData] = useState<MaterialWithCalculations[]>([])
  const [inventoryLoading, setInventoryLoading] = useState(false)
  const [inventoryStats, setInventoryStats] = useState<InventoryStats>({
    totalValue: 0,
    totalItems: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    averageValue: 0
  })

  useEffect(() => {
    fetchInventoryData()
  }, [])

  const fetchInventoryData = async () => {
    setInventoryLoading(true)
    try {
      const response = await materialsApi.getAll()
      let materials: any[] = []
      
      if (response && Array.isArray(response.data)) {
        materials = response.data
      } else if (Array.isArray(response)) {
        materials = response
      }

      // Calculate inventory statistics
      const totalValue = materials.reduce((sum, material) => {
        const stock = Number(material.currentStock) || 0
        const cost = Number(material.averageCost) || Number(material.lastPurchasePrice) || Number(material.lastCostPerUnit) || 0
        return sum + (stock * cost)
      }, 0)

      const totalItems = materials.length
      const lowStockItems = materials.filter(m => {
        const stock = Number(m.currentStock) || 0
        const reorderPoint = Number(m.reorderPoint) || 0
        return stock > 0 && stock <= reorderPoint
      }).length

      const outOfStockItems = materials.filter(m => {
        const stock = Number(m.currentStock) || 0
        return stock === 0
      }).length

      setInventoryStats({
        totalValue,
        totalItems,
        lowStockItems,
        outOfStockItems,
        averageValue: totalItems > 0 ? totalValue / totalItems : 0
      })

      // Sort materials by value (stock * cost) descending
      const sortedMaterials = materials
        .map(material => {
          const stock = Number(material.currentStock) || 0
          const cost = Number(material.averageCost) || Number(material.lastPurchasePrice) || Number(material.lastCostPerUnit) || 0
          const totalValue = stock * cost
          const reorderPoint = Number(material.reorderPoint) || 0
          
          return {
            ...material,
            calculatedValue: totalValue,
            stockStatus: stock === 0 ? 'out-of-stock' : stock <= reorderPoint ? 'low-stock' : 'in-stock'
          } as MaterialWithCalculations
        })
        .sort((a, b) => b.calculatedValue - a.calculatedValue)

      setInventoryData(sortedMaterials)
    } catch (error) {
      console.error('Error fetching inventory data:', error)
      setInventoryData([])
    } finally {
      setInventoryLoading(false)
    }
  }

  const exportInventoryToCsv = () => {
    const headers = [
      'Material Name',
      'Category',
      'Current Stock',
      'Reorder Point',
      'Average Cost',
      'Total Value',
      'Stock Status',
      'Last Purchase Date',
      'Supplier'
    ]
    
    const rows = inventoryData.map(material => [
      material.name,
      material.category,
      material.currentStock,
      material.reorderPoint || 'N/A',
      (Number(material.averageCost) || Number(material.lastPurchasePrice) || 0).toFixed(4),
      material.calculatedValue.toFixed(2),
      material.stockStatus.replace('-', ' ').toUpperCase(),
      material.lastPurchaseDate ? new Date(material.lastPurchaseDate).toLocaleDateString() : 'N/A',
      material.preferredSupplier || 'N/A'
    ])

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n")

    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', `inventory_analysis_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Inventory Analysis
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive inventory valuation and stock level analysis
          </p>
        </div>
        <Button
          size="sm"
          onClick={exportInventoryToCsv}
          leftIcon={<Download className="w-4 h-4" />}
          disabled={inventoryLoading || inventoryData.length === 0}
        >
          Export CSV
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Value</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            ${inventoryStats.totalValue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Items</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {inventoryStats.totalItems}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Low Stock</div>
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {inventoryStats.lowStockItems}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Out of Stock</div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {inventoryStats.outOfStockItems}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">Avg. Value</div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            ${inventoryStats.averageValue.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Material</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Category</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Stock</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reorder Point</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Unit Cost</th>
                <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Value</th>
                <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {inventoryLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      Loading inventory data...
                    </div>
                  </td>
                </tr>
              ) : inventoryData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No inventory data found.
                  </td>
                </tr>
              ) : (
                inventoryData.map(material => (
                  <tr key={material.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                      {material.name}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {material.category}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                      {Number(material.currentStock).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600 dark:text-gray-400">
                      {material.reorderPoint ? Number(material.reorderPoint).toFixed(2) : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-900 dark:text-white">
                      ${(Number(material.averageCost) || Number(material.lastPurchasePrice) || 0).toFixed(4)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-gray-900 dark:text-white">
                      ${material.calculatedValue.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        material.stockStatus === 'out-of-stock'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : material.stockStatus === 'low-stock'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {material.stockStatus.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}